import { readProjectSettings } from '../admin/adminSettings';
import {
    EXAM_TASK_DESTINATION_RESOURCE,
    EXAM_TASK_REVIEW_RESOURCE,
    type ExamTaskResource,
} from '../admin/examTaskReviewDataProvider';

export type ExamTaskWorkflowId = 'review' | 'destination';

export type ExamTaskWorkflowAction = {
    key: string;
    label: string;
    pendingLabel: string;
    actionPath: string;
    requestMethod?: 'POST' | 'DELETE';
    color: 'success' | 'error' | 'primary';
    removesRecord?: boolean;
    requiresConfirmation?: boolean;
    confirmationMessage?: (examTaskLabel: string) => string;
    successMessage: (examTaskLabel: string) => string;
    errorMessage: string;
};

export type ExamTaskWorkflow = {
    id: ExamTaskWorkflowId;
    resource: ExamTaskResource;
    routeBase: string;
    menuLabel: string;
    pageTitle: string;
    detailTitle: string;
    listDescription: (examName: string, apiType?: string) => string;
    detailDescription: (recordId?: string) => string;
    missingControllerMessage: string | null;
    actions: ExamTaskWorkflowAction[];
};

function trimSlashes(value: string): string {
    return value.trim().replace(/^\/+|\/+$/g, '');
}

export function getExamTaskPublishTarget() {
    const environment = String(import.meta.env.VITE_ENVMENT ?? '').trim().toLowerCase();
    const noun = environment === 'dev' ? 'stage' : 'production';

    return {
        noun,
        title: noun.charAt(0).toUpperCase() + noun.slice(1),
    };
}

export function getVisibleExamTaskWorkflowActions(
    workflow: ExamTaskWorkflow,
    isActive: boolean,
): ExamTaskWorkflowAction[] {
    if (workflow.id !== 'destination') {
        return workflow.actions;
    }

    return workflow.actions.filter((action) => {
        if (action.key === 'primary') {
            return false;
        }

        if (action.key === 'activate') {
            return !isActive;
        }

        if (action.key === 'deactivate') {
            return isActive;
        }

        return true;
    });
}

export function getExamTaskWorkflow(workflowId: ExamTaskWorkflowId): ExamTaskWorkflow {
    if (workflowId === 'review') {
        return {
            id: 'review',
            resource: EXAM_TASK_REVIEW_RESOURCE,
            routeBase: '/exam-tasks',
            menuLabel: 'Review Exam Tasks',
            pageTitle: 'Review Exam Tasks',
            detailTitle: 'Exam Task Review Detail',
            listDescription: (examName, apiType) =>
                `Review queue for ${examName} exam tasks using API type ${apiType ?? 'unknown'}.`,
            detailDescription: (recordId) => `Review detail for exam task ${recordId ?? 'unknown'}.`,
            missingControllerMessage: null,
            actions: [
                {
                    key: 'approve',
                    label: 'Accept',
                    pendingLabel: 'Accepting...',
                    actionPath: 'approve',
                    color: 'success',
                    successMessage: (examTaskLabel) => `${examTaskLabel} approved`,
                    errorMessage: 'Failed to approve exam task',
                },
                {
                    key: 'reject',
                    label: 'Reject',
                    pendingLabel: 'Rejecting...',
                    actionPath: 'reject',
                    color: 'error',
                    successMessage: (examTaskLabel) => `${examTaskLabel} rejected`,
                    errorMessage: 'Failed to reject exam task',
                },
            ],
        };
    }

    const settings = readProjectSettings();
    const publishTarget = getExamTaskPublishTarget();
    const controller = trimSlashes(
        String(import.meta.env.VITE_DESTINATION_EXAM_TASK_API_CONTROLLER ?? settings.destinationExamTaskApiController),
    );
    const primaryActionPath = trimSlashes(settings.destinationExamTaskPrimaryActionPath);

    return {
        id: 'destination',
        resource: EXAM_TASK_DESTINATION_RESOURCE,
        routeBase: '/live-exam-tasks',
        menuLabel: 'Manage Live Exam Tasks',
        pageTitle: 'Manage Live Exam Tasks',
        detailTitle: 'Live Exam Task Detail',
        listDescription: (examName, apiType) =>
            `Live exam tasks for ${examName} using API type ${apiType ?? 'unknown'}.`,
        detailDescription: (recordId) => `Manage live exam task ${recordId ?? 'unknown'}.`,
        missingControllerMessage: controller
            ? null
            : 'Live exam task API controller is not configured in Project Settings.',
        actions: [
            {
                key: 'primary',
                label: settings.destinationExamTaskPrimaryActionLabel.trim() || 'Save',
                pendingLabel: `${settings.destinationExamTaskPrimaryActionLabel.trim() || 'Save'}...`,
                actionPath: primaryActionPath,
                color: 'primary',
                successMessage: (examTaskLabel) => `${examTaskLabel} updated`,
                errorMessage: `Failed to ${primaryActionPath || 'run primary action'} for exam task`,
            },
            {
                key: 'activate',
                label: 'Activate',
                pendingLabel: 'Activating...',
                actionPath: 'activate',
                requestMethod: 'POST',
                color: 'success',
                successMessage: (examTaskLabel) => `${examTaskLabel} activated`,
                errorMessage: 'Failed to activate exam task',
            },
            {
                key: 'deactivate',
                label: 'Deactivate',
                pendingLabel: 'Deactivating...',
                actionPath: 'deactivate',
                requestMethod: 'POST',
                color: 'error',
                successMessage: (examTaskLabel) => `${examTaskLabel} deactivated`,
                errorMessage: 'Failed to deactivate exam task',
            },
            {
                key: 'publish',
                label: `Publish to ${publishTarget.title}`,
                pendingLabel: `Publishing to ${publishTarget.title}...`,
                actionPath: 'publish',
                requestMethod: 'POST',
                color: 'success',
                successMessage: (examTaskLabel) => `${examTaskLabel} published to ${publishTarget.noun}`,
                errorMessage: `Failed to publish exam task to ${publishTarget.noun}`,
            },
            {
                key: 'delete',
                label: 'Delete',
                pendingLabel: 'Deleting...',
                actionPath: 'delete',
                requestMethod: 'DELETE',
                color: 'error',
                removesRecord: true,
                requiresConfirmation: true,
                confirmationMessage: (examTaskLabel) => `Delete ${examTaskLabel}? This cannot be undone.`,
                successMessage: (examTaskLabel) => `${examTaskLabel} deleted`,
                errorMessage: 'Failed to delete exam task',
            },
        ],
    };
}